import { Column } from 'react-table';
import { groupBy } from 'lodash';
import { DateTime } from 'luxon';
import { Box, Typography } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { WorkersAvailabilityPaginated } from 'api/services/availability/availability-service.schema';
import { AvailabilityGridCell } from './availability-grid-cell';
import { CalendarGrid } from '../calendar/calendar-grid';
import { getGridColDefs } from '../availability/helpers/get-calendar-grid-column-definitions';
import { CalendarModes, dayAccessors, DayColumnAccessors } from '../calendar/calendar-grid.types';
import {
  AvailabilityParsed,
  AvailabilityParsedItem,
} from '../availability/availability-calendar.types';

export const AvailabilityCalendarGrid = ({
  flatData,
  selectedDate,
  calendarMode,
  onScroll,
}: {
  flatData: WorkersAvailabilityPaginated['items'];
  selectedDate: DateTime;
  calendarMode: CalendarModes;
  onScroll?: (e: React.UIEvent<HTMLDivElement>) => void;
}) => {
  const startDate = useMemo(
    () => (selectedDate ? selectedDate : DateTime.now().startOf(calendarMode)),
    [selectedDate]
  );
  const availabilityColumnDefinitions: Column<AvailabilityParsed<DayColumnAccessors>>[] = useMemo(
    () => getGridColDefs(),
    []
  );
  const cellRenderer = useCallback(
    ({ value }: { value: AvailabilityParsedItem[] | undefined }) =>
      availabilityCellRenderer({ value }),
    []
  );
  const availabilityData: AvailabilityParsed<DayColumnAccessors>[] = useMemo(
    () => mapAvailabilityResponseToParsedGridDataMonthView(flatData),
    [flatData]
  );
  return flatData.length > 0 ? (
    <CalendarGrid<AvailabilityParsed<DayColumnAccessors>, AvailabilityParsedItem[]>
      columns={availabilityColumnDefinitions}
      data={availabilityData}
      mode={'month'}
      cellFn={cellRenderer}
      startDate={startDate}
      onScroll={onScroll}
    />
  ) : (
    <Box>
      <Typography>Er zijn geen flexwerkers gevonden!</Typography>
      <Typography>Met de huidige filters zijn er geen flexwerkers gevonden.</Typography>
    </Box>
  );
};

//At some point, when there are more calendar modes, this can extend a union type of accessors
const mapAvailabilityResponseToParsedGridDataMonthView = (
  workerAvailabilities: WorkersAvailabilityPaginated['items']
): AvailabilityParsed<DayColumnAccessors>[] => {
  return workerAvailabilities.map((worker) => {
    const workerAvailabilityItem: AvailabilityParsedItem[] = worker.availability.map(
      (availability) => ({
        type: availability.type,
        utcStartDateTime: availability.utcStartDateTime,
        utcEndDateTime: availability.utcEndDateTime,
        workerForPopover: {
          workerId: worker.workerId,
          firstName: worker.firstName,
          infix: worker.infix,
          lastName: worker.lastName,
          profilePictureUrl: worker.profilePictureUrl,
          email: worker.email,
        },
      })
    );
    const groupedAvailability: Partial<Record<DayColumnAccessors, AvailabilityParsedItem[]>> =
      groupBy<AvailabilityParsedItem>(
        workerAvailabilityItem,
        (item) => `DAY_${DateTime.fromISO(item.utcStartDateTime).day.toString()}`
      );
    const allEntries: Record<DayColumnAccessors, AvailabilityParsedItem[] | undefined> =
      dayAccessors.reduce(
        (
          acc: Record<DayColumnAccessors, AvailabilityParsedItem[] | undefined>,
          day: DayColumnAccessors
        ) => {
          acc[day] = groupedAvailability[day];
          return acc;
        },
        {} as Record<DayColumnAccessors, AvailabilityParsedItem[] | undefined>
      );
    const parsedItem: AvailabilityParsed<DayColumnAccessors> = {
      worker: {
        workerId: worker.workerId,
        firstName: worker.firstName,
        infix: worker.infix,
        lastName: worker.lastName,
        profilePictureUrl: worker.profilePictureUrl,
        email: worker.email,
      },
      ...allEntries,
    };
    return parsedItem;
  });
};

const availabilityCellRenderer = ({ value }: { value: AvailabilityParsedItem[] | undefined }) => {
  return <AvailabilityGridCell availabilityBlocks={value ? value : []} />;
};
